<script setup lang="ts">
// Compact pricing band for the homepage. Prices and the payment mode come from
// the same sources as /pricing (PRICES_EUR via useCurrency, runtime
// paymentMode), so the teaser can never disagree with the pricing page.
const runtimeConfig = useRuntimeConfig()
const { formatPrice } = useCurrency()

const isOnetime = computed(() => ((runtimeConfig.public.paymentMode as string) || 'onetime') === 'onetime')

const plans = computed(() => [
  { name: 'Free', price: formatPrice('free', 'lifetime'), note: 'forever' },
  { name: 'Pro', price: formatPrice('pro', isOnetime.value ? 'lifetime' : 'monthly'), note: isOnetime.value ? 'paid once' : 'per month' },
  { name: 'Elite', price: formatPrice('elite', isOnetime.value ? 'lifetime' : 'monthly'), note: isOnetime.value ? 'paid once' : 'per month' }
])

const title = computed(() => isOnetime.value ? 'Start free, upgrade once' : 'Start free, upgrade when you want')
const description = computed(() => isOnetime.value
  ? 'The free plan is free forever. Pro and Elite are one-time payments: pay once and keep the plan, with no subscription.'
  : 'The free plan is free forever. Pro and Elite add more customization and higher limits.')
</script>

<template>
  <UContainer class="py-16 sm:py-20">
    <div class="grid items-center gap-8 rounded-2xl border border-default bg-elevated/40 p-6 shadow-(--surface-shadow) sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-12">
      <div>
        <p class="text-sm font-semibold text-primary">
          Pricing
        </p>
        <h2 class="mt-2 text-2xl font-semibold text-highlighted sm:text-3xl">
          {{ title }}
        </h2>
        <p class="mt-3 max-w-xl text-base text-muted">
          {{ description }}
        </p>
      </div>

      <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
        <dl class="grid grid-cols-3 divide-x divide-default rounded-xl border border-default bg-default/60">
          <div
            v-for="plan in plans"
            :key="plan.name"
            class="px-5 py-3.5 text-center"
          >
            <dt class="text-xs font-medium text-muted">
              {{ plan.name }}
            </dt>
            <dd class="mt-1 text-lg font-semibold tabular-nums text-highlighted">
              {{ plan.price }}
            </dd>
            <dd class="text-xs text-dimmed">
              {{ plan.note }}
            </dd>
          </div>
        </dl>
        <UButton
          label="Compare plans"
          trailing-icon="i-lucide-arrow-right"
          color="neutral"
          variant="outline"
          size="lg"
          to="/pricing"
          class="justify-center"
        />
      </div>
    </div>
  </UContainer>
</template>
